import { useCallback, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import { ShippingTracker } from '#types';
import { useOrganizationParameters } from '../../../../use/useOrganizationParameters';
import { shippingCarriers } from './shippingCarriers';
import { callSyncShippingTrackerFromEasyPost } from './callSyncShippingTrackerFromEasyPost';
import { callSyncShippingTrackerFrom17Track } from './callSyncShippingTrackerFrom17Track';
import { resolveTrackingUrlForShippingValue } from './resolveTrackingUrlForShippingValue';

interface UseShippingTrackerUIProps {
    value?: string | ShippingTracker | null | undefined;
    onChange?: (value: ShippingTracker | null) => void;
}

export const useShippingTrackerUI = ({ value, onChange }: UseShippingTrackerUIProps) => {
    const trackerValue = useMemo(() => parseValue(value), [value]);
    const { data: orgParams } = useOrganizationParameters();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState<string | null>(null);

    const handleComponentChange = useCallback((components: ShippingTracker['components'], nextMeta?: ShippingTracker['meta']) => {
        const trackingNumber = (components?.trackingNumber ?? '').trim();
        if (!trackingNumber) {
            onChange?.(null);
            return;
        }

        const normalizedComponents: ShippingTracker['components'] = {
            ...components,
            trackingNumber,
            carrierId: components?.carrierId || 'other',
        };

        const mergedMeta = mergeMeta(trackerValue.meta, nextMeta);

        onChange?.({
            formatted: formatTrackerDisplay(normalizedComponents),
            components: normalizedComponents,
            meta: mergedMeta,
        });
    }, [onChange, trackerValue.meta]);

    const handleClear = useCallback(() => {
        onChange?.(null);
    }, [onChange]);

    const getCarrierInfo = useCallback((carrierId: string) => {
        return shippingCarriers.find((c) => c.id === carrierId) || shippingCarriers.find((c) => c.id === 'other');
    }, []);

    const getTrackingUrl = useCallback((trackingNumber: string, carrierId: string) => {
        if (!trackingNumber) return null;
        return resolveTrackingUrlForShippingValue({
            components: {
                trackingNumber,
                carrierId,
            },
        });
    }, []);

    const calculateProgress = useCallback((currentStatus?: string) => {
        if (!currentStatus) return 0;

        const normalized = normalizeStatus(currentStatus);
        if (normalized.includes('delivered')) return 100;
        if (normalized.includes('outfordelivery')) return 90;
        if (normalized.includes('availableforpickup')) return 85;
        if (normalized.includes('intransit')) return 50;
        if (normalized.includes('inforeceived') || normalized.includes('pretransit')) return 25;

        if (
            normalized.includes('deliveryfailure') ||
            normalized.includes('failure') ||
            normalized.includes('exception') ||
            normalized.includes('returntosender')
        ) return 15;

        return 10;
    }, []);

    const provider = useMemo(() => {
        const v = (orgParams as any)?.shippingTrackingProvider as string | undefined;
        if (v === 'seventeentrack') return 'seventeentrack' as const;
        if (v === 'auto') return 'auto' as const;
        return 'easypost' as const;
    }, [orgParams]);

    const providerLabel = useMemo(() => {
        if (provider === 'seventeentrack') return '17Track';
        if (provider === 'auto') return 'Auto';
        return 'EasyPost';
    }, [provider]);

    const refreshFromProvider = useCallback(async (opts?: {
        trackingNumber?: string;
        carrierId?: string;
        force?: boolean;
    }) => {
        const trackingNumber = (opts?.trackingNumber ?? trackerValue?.components?.trackingNumber ?? '').trim();
        const carrierId = (opts?.carrierId ?? trackerValue?.components?.carrierId ?? 'other').trim() || 'other';
        const force = !!opts?.force;

        if (!trackingNumber) {
            setRefreshError('Tracking number is required');
            return;
        }

        if (!force) {
            const lastSyncedAtMs = readLastSyncedAtMs(trackerValue?.meta);
            if (lastSyncedAtMs && Date.now() - lastSyncedAtMs < 2 * 60 * 1000) {
                return; // too soon; rely on cached snapshot
            }
        }

        try {
            setIsRefreshing(true);
            setRefreshError(null);

            const resolvedProvider = provider === 'auto'
                ? (carrierId === 'other' ? 'seventeentrack' : 'easypost')
                : provider;

            const res = resolvedProvider === 'seventeentrack'
                ? await callSyncShippingTrackerFrom17Track({ trackingNumber, carrierId, force })
                : await callSyncShippingTrackerFromEasyPost({ trackingNumber, carrierId, force });

            const comps = res?.components;
            if (!comps?.trackingNumber) throw new Error(`${resolvedProvider === 'seventeentrack' ? '17Track' : 'EasyPost'} response missing tracking number`);

            const convertedComponents: ShippingTracker['components'] = {
                trackingNumber: comps.trackingNumber,
                carrierId: comps.carrierId || carrierId,
                currentStatus: comps.currentStatus,
                deliveryDate: typeof comps.deliveryDateMs === 'number'
                    ? Timestamp.fromMillis(comps.deliveryDateMs)
                    : undefined,
                origin: comps.origin,
                destination: comps.destination,
                additionalDetails: comps.additionalDetails as any,
                trackingHistory: Array.isArray(comps.trackingHistory)
                    ? comps.trackingHistory
                        .filter((e) => typeof e?.timestampMs === 'number' && !!e?.status)
                        .map((e) => ({
                            timestamp: Timestamp.fromMillis(e.timestampMs),
                            status: (e.status || '').trim(),
                            location: (e.location || '').trim() || undefined,
                            details: (e.details || '').trim() || undefined,
                        }))
                    : undefined,
            };

            const nextMeta: ShippingTracker['meta'] = {
                ...(res?.meta as any),
                easypost: resolvedProvider === 'easypost'
                    ? {
                        ...(((res?.meta as any)?.easypost) ?? {}),
                        lastSyncedAt: readProviderLastSyncedAtTimestamp(res?.meta, 'easypost'),
                    }
                    : undefined,
                seventeenTrack: resolvedProvider === 'seventeentrack'
                    ? {
                        ...(((res?.meta as any)?.seventeenTrack) ?? {}),
                        lastSyncedAt: readProviderLastSyncedAtTimestamp(res?.meta, 'seventeenTrack'),
                    }
                    : undefined,
            };

            handleComponentChange(convertedComponents, nextMeta);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to refresh tracking';
            setRefreshError(message);
        } finally {
            setIsRefreshing(false);
        }
    }, [handleComponentChange, provider, trackerValue?.components?.carrierId, trackerValue?.components?.trackingNumber, trackerValue?.meta]);

    return {
        trackerValue,
        drawerOpen,
        setDrawerOpen,
        handleComponentChange,
        handleClear,
        getCarrierInfo,
        getTrackingUrl,
        calculateProgress,
        refreshFromProvider,
        providerLabel,
        isRefreshing,
        refreshError,
    };
};

function parseValue(value: string | ShippingTracker | null | undefined): ShippingTracker {
    if (!value) {
        return {
            formatted: '',
            components: {
                trackingNumber: '',
                carrierId: 'other'
            }
        };
    }

    if (typeof value === 'string') {
        const trackingNumber = value.trim();
        return {
            formatted: trackingNumber,
            components: {
                trackingNumber,
                carrierId: 'other'
            }
        };
    }

    return value;
}

function formatTrackerDisplay(components?: ShippingTracker['components']): string {
    if (!components || !components.trackingNumber) return '';

    const carrier = shippingCarriers.find((c) => c.id === components.carrierId);
    const carrierName = carrier?.name || 'Unknown Carrier';
    const trackingNum = components.trackingNumber;
    const status = components.currentStatus || '';
    
    let display = `${carrierName} ${trackingNum}`;
    
    if (components.deliveryDate) {
        const date = components.deliveryDate instanceof Timestamp
            ? dayjs(components.deliveryDate.toDate()).format('MMM D, YYYY')
            : 'Invalid Date';
        display += ` - ${status || 'Expected'} ${date}`;
    } else if (status) {
        display += ` - ${status}`;
    }
    
    return display;
}

function mergeMeta(currentMeta?: ShippingTracker['meta'], nextMeta?: ShippingTracker['meta']): ShippingTracker['meta'] | undefined {
    if (!currentMeta && !nextMeta) return undefined;
    const merged: any = { ...(currentMeta ?? {}), ...(nextMeta ?? {}) };
    if ((currentMeta as any)?.easypost || (nextMeta as any)?.easypost) {
        merged.easypost = { ...((currentMeta as any)?.easypost ?? {}), ...((nextMeta as any)?.easypost ?? {}) };
    }
    if ((currentMeta as any)?.seventeenTrack || (nextMeta as any)?.seventeenTrack) {
        merged.seventeenTrack = { ...((currentMeta as any)?.seventeenTrack ?? {}), ...((nextMeta as any)?.seventeenTrack ?? {}) };
    }
    return merged;
}

function readLastSyncedAtMs(meta?: ShippingTracker['meta']): number | undefined {
    const easypost = (meta as any)?.easypost;
    const ts = easypost?.lastSyncedAt;
    if (typeof easypost?.lastSyncedAtMs === 'number') return easypost.lastSyncedAtMs;
    if (ts instanceof Timestamp) return ts.toMillis();
    return undefined;
}

function readProviderLastSyncedAtTimestamp(meta: unknown, providerKey: 'easypost' | 'seventeenTrack'): Timestamp | undefined {
    const provider = (meta as any)?.[providerKey];
    const ms = provider?.lastSyncedAtMs;
    if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0) {
        return Timestamp.fromMillis(ms);
    }
    return undefined;
}

function normalizeStatus(status: string): string {
    return status
        .trim()
        .toLowerCase()
        .replaceAll('_', '')
        .replaceAll(' ', '');
}
